import React from "react";
import { useTranslation } from "react-i18next";
import type { SiteMatch } from "../../api/sites";

export interface SiteSectionAddress {
  street: string;
  number: string;
  zip: string;
  city: string;
  country: string;
}

export type SiteSectionMode = "matched" | "separate" | "create";

interface SiteSectionProps {
  // Onboarding direction. "both" (combined EAN) always creates a single new site.
  direction: "injection" | "offtake" | "both";
  // The existing site to preselect (single-direction only); null ⇒ create path.
  match: SiteMatch | null;
  mode: SiteSectionMode;
  siteName: string;
  suggestedName: string;
  address: SiteSectionAddress;
  loading?: boolean;
  showNameError?: boolean;
  onSiteNameChange: (name: string) => void;
  onUseSeparate: () => void;
  onUseExisting: () => void;
}

const joinParts = (parts: (string | null | undefined)[]) =>
  parts.map((p) => (p ?? "").trim()).filter(Boolean).join(" ");

const formatAddress = (a: {
  street?: string | null;
  number?: string | null;
  zip?: string | null;
  city?: string | null;
  country?: string | null;
}): string =>
  [joinParts([a.street, a.number]), joinParts([a.zip, a.city]), (a.country ?? "").trim()]
    .filter(Boolean)
    .join(", ");

export const SiteSection: React.FC<SiteSectionProps> = ({
  direction,
  match,
  mode,
  siteName,
  suggestedName,
  address,
  loading = false,
  showNameError = false,
  onSiteNameChange,
  onUseSeparate,
  onUseExisting,
}) => {
  const { t } = useTranslation("addservice");

  const hasMatch = direction !== "both" && match !== null;
  const showMatchedCard = hasMatch && mode === "matched";
  const directionLabel = direction === "both" ? "" : t(`site.direction.${direction}`);

  return (
    <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
      <div className="font-semibold text-primary-600 text-base mb-3">{t("site.heading")}</div>

      {loading ? (
        <div className="text-primary-400 text-sm">{t("site.loading")}</div>
      ) : showMatchedCard && match ? (
        <div>
          <div className="p-3 bg-white rounded border border-primary-100">
            <p className="text-primary-600 text-sm">
              {t("site.attachExisting", {
                direction: directionLabel,
                name: match.name,
                address: formatAddress(match.address),
              })}
            </p>
          </div>
          <p className="text-primary-400 text-xs mt-2">{t("site.separateExplain")}</p>
          <button
            type="button"
            onClick={onUseSeparate}
            className="mt-3 text-primary-600 text-sm underline hover:text-primary-700"
          >
            {t("site.useSeparate")}
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <label htmlFor="site-name" className="text-primary-600 font-medium text-sm">
            {t("site.siteName")} <span className="text-red-600">*</span>
          </label>
          <input
            id="site-name"
            type="text"
            value={siteName}
            onChange={(e) => onSiteNameChange(e.target.value)}
            className="border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200 focus:ring-opacity-20"
          />
          {showNameError && <p className="text-xs text-red-600">{t("site.nameRequired")}</p>}
          <div className="text-primary-500 text-xs">{formatAddress(address)}</div>
          {suggestedName && (
            <span className="text-xs text-gray-400">{t("site.suggestedHint", { name: suggestedName })}</span>
          )}
          {hasMatch && (
            <>
              <p className="text-primary-400 text-xs mt-1">{t("site.separateExplain")}</p>
              <button
                type="button"
                onClick={onUseExisting}
                className="self-start text-primary-600 text-sm underline hover:text-primary-700"
              >
                {t("site.useExisting")}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
